import { lazy, Suspense } from "react";
import { useTranslation } from "react-i18next";
import styles from "../../SsnCardVerification.module.scss";
import type { TerminalSsnCardErrorCode } from "../../../../shared/types/ssnCardVerification";
import { usePageView } from "../../../../shared/analytics/usePageView";
import { trackClicked } from "../../../../shared/analytics/events";
import { DOCUMENT_CAPTURE_EVENTS, DOCUMENT_CAPTURE_SECTIONS } from "../../../../shared/analytics/documentCaptureEvents";

// Lazy so the Regula WASM wrapper never enters the parent UMD bundle — it loads
// only when the capture screen actually mounts.
const RegulaCapture = lazy(() =>
  import("../../regula/auto/RegulaCapture").then((m) => ({ default: m.RegulaCapture })),
);

interface CaptureScreenProps {
  /** Clean capture — hand both cropped images (raw base64) to the hook to POST. */
  onSubmit: (frontBase64: string, backBase64: string) => void;
  /**
   * Terminal failure — propagated to the parent. `feedback` carries the specific
   * capture reason behind a generic code (e.g. ssn_card_verification_failed).
   */
  onError: (code: TerminalSsnCardErrorCode, message: string, feedback?: string) => void;
}

/**
 * CaptureScreen — the full-screen capture surface (state `capturing`). Mounts the
 * Regula `<document-reader>` (Option-3 single multipage mount).
 *
 * There is NO local retry loop: every failure is terminal. A bad capture reports a
 * generic `ssn_card_verification_failed` with a specific `feedback` reason (the iProov
 * pattern); the parent (IVA) decides whether to relaunch. Nothing is POSTed until a
 * clean capture — bad images never leave the device.
 *
 * - clean capture          → onSubmit(front, back) (parent flips to `processing`)
 * - any capture failure     → onError(code, message, feedback) to the parent
 * - user closed the reader  → onError("user_canceled") (mirrors legal-id's Veriff cancel)
 */
export function CaptureScreen({ onSubmit, onError }: CaptureScreenProps) {
  const { t } = useTranslation();
  usePageView(DOCUMENT_CAPTURE_EVENTS.VIEWED.DOCUMENT_CAPTURE_SCREEN, "section", { section: DOCUMENT_CAPTURE_SECTIONS.DOCUMENT_CAPTURE });

  // Clean capture: hand both images to the hook. The hook flips to `processing`,
  // which unmounts this screen — so we don't render an intermediate state here.
  const handleCapture = (front: { base64: string }, back: { base64: string }) => {
    onSubmit(front.base64, back.base64);
  };

  // User closed the reader — terminal, propagated to the parent (mirrors legal-id's
  // Veriff "user_canceled"). The parent/IVA decides whether to relaunch.
  const handleCancel = () => {
    trackClicked(DOCUMENT_CAPTURE_EVENTS.CLICKED.CLOSE_DOCUMENT_CAPTURE, "button", { section: DOCUMENT_CAPTURE_SECTIONS.DOCUMENT_CAPTURE, targetAction: "navigate" });
    onError("user_canceled", t("ssnCard.error.canceled"));
  };

  return (
    <div className={styles.captureScreen}>
      <h2 className={styles.captureHeading}>{t("ssnCard.capturing.heading")}</h2>
      <div id="ssn-card-capture-root" className={styles.captureReader}>
        <Suspense
          fallback={
            <div className={styles.spinner} role="status" aria-label={t("ssnCard.capturing.subtitle")} />
          }
        >
          <RegulaCapture onCapture={handleCapture} onError={onError} onCancel={handleCancel} />
        </Suspense>
      </div>
    </div>
  );
}
